import { Box, Button, InputAdornment, Typography } from '@mui/material'
import React, { forwardRef, useState } from 'react'
import { IconButton, TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { tLabel, useMessage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, SubmitHandler, useForm, UseFormProps } from '@/core/form'

export type WalletPasswordSettingFormProps = {
  email?: string | null
  onSubmit: SubmitHandler<WalletPasswordFormValues>
  onClose?: () => void
}

export type WalletPasswordFormValues = {
  password: string
}

const schema: UseFormProps['schema'] = o => ({
  password: o.string().min(8).label(tLabel('passwordSettings.password')),
})

const defaultValues: WalletPasswordFormValues = {
  password: '',
}

export const WalletPasswordSettingForm: Organism<WalletPasswordSettingFormProps> =
  ({ email, onSubmit, onClose }) => {
    const { t } = useTranslation(['walletPasswordSettings', 'firebase'])
    const {
      handleSubmit,
      register,
      formState: { errors, isSubmitting },
      reset,
    } = useForm<WalletPasswordFormValues>({
      schema,
      mode: 'onChange',
      defaultValues,
    })
    const { showMessage, showError } = useMessage()

    return (
      <Form
        onSubmit={handleSubmit(
          inputs =>
            onSubmit(inputs)
              .then(() => {
                showMessage(
                  t('walletPasswordSettings:registerSuccess'),
                  'success'
                )
                reset()
                onClose && onClose()
              })
              .catch((e: Error) => showError(e)),
          e => {
            showError(new Error(t('emailAddressSettings:error')))
            throw e
          }
        )}
      >
        <Box sx={{ pt: 2 }}>
          <Typography sx={{ pb: 1 }} variant="subtitle1" color="text.secondary">
            {t('currentEmailAddress')}
          </Typography>
          <Typography sx={{ pb: 7 }} variant="body1">
            {email}
          </Typography>
        </Box>
        <PasswordInputCell error={errors?.password} {...register('password')} />

        <ButtonCell loading={isSubmitting} />
      </Form>
    )
  }

const PasswordInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't' | 'type'>, ref) => {
    const { t } = useTranslation('walletPasswordSettings')
    const [showPassword, setShowPassword] = useState(false)

    return (
      <CellFrame>
        <TextInputField
          type={showPassword ? 'text' : 'password'}
          {...props}
          textFieldProps={{
            ...props.textFieldProps,
            InputProps: {
              ...props.textFieldProps?.InputProps,
              inputProps: { variant: 'body2', sx: { px: 4, py: 3 } },
              endAdornment: (
                <InputAdornment position="end">
                  {showPassword ? (
                    <IconButton.Display
                      onClick={() => setShowPassword(prevState => !prevState)}
                    />
                  ) : (
                    <IconButton.Hide
                      onClick={() => setShowPassword(prevState => !prevState)}
                    />
                  )}
                </InputAdornment>
              ),
            },
          }}
          ref={ref}
          t={t}
        />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  loading: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading }) => {
  const { t } = useTranslation('walletPasswordSettings')

  return (
    <CellFrame sx={{ px: 4, py: 3, textAlign: 'center' }}>
      <Button type="submit" size="large" sx={{ width: 244 }} loading={loading}>
        {t('submit')}
      </Button>
    </CellFrame>
  )
}
